import { FC, FormEvent, useEffect, useState } from 'react';
import './login.css';
import { LoginProps } from './login.type';
import { useLoginData } from '../../hooks/useAuthData';
import { renderDesktopView, renderTabletView } from './loginHelper';
import { useTranslation } from 'react-i18next';
import {
  apiStatusCode,
  LoginPlaceholder,
  LoginToggleKey,
  StaffPinLength,
  StorageKey,
  SuccessMessage,
} from '../../utils/helper.type';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useLocation, useNavigate } from 'react-router-dom';
import { route } from '../../service/router';
import ForgotPinModal from '../forgotPassword/forgotPinModal';
import { LoginResponse, UserType } from '@inplay/models';
import { useAuth } from '../../service/context/authContext';
import AgreementModal from '../../component/agreementModal/agreementModal';
import SuccessToast from '../../component/successModal/successModal';
import { jwtDecode } from 'jwt-decode';
import { useLoginAsStaff } from '../../hooks/useUserData';
import { LoginAsStaffResponseData } from '../../service/service.type';
import { checkVenueSetupNeeded } from '../../utils/helper';

const Login: FC<LoginProps> = () => {
  const location = useLocation();
  const staffAccessToken: string | null = localStorage.getItem(
    StorageKey.STAFF_ACCESS_TOKEN,
  );

  const { isDesktop } = useResponsiveLayout();
  const [email, setEmail] = useState<string>('');
  const [activeLoginKey, setActiveLoginKey] = useState<LoginToggleKey>(
    LoginToggleKey.STAFF,
  );
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showResetSuccessToast, setShowResetSuccessToast] = useState<boolean>(
    location.state?.showResetSuccessToast ?? false,
  );
  const [showNewPasswordSuccessToast, setShowNewPasswordSuccessToast] =
    useState<boolean>(location.state?.showNewPasswordSuccessToast ?? false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en-uk');
  const { login, isLoading, error } = useLoginData();
  const { loginAsStaffError, loginAsStaffLoading, loginAsStaff } =
    useLoginAsStaff();
  const { isDisplayPIN, setIsDisplayPIN } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const loginTranslation: LoginPlaceholder =
    i18n.getResourceBundle(i18n.language, 'translation')?.login.placeholder ||
    {};
  const [pinLogin, setPinLogin] = useState<string>('');
  const [isShowTerm, setIsShowTerm] = useState<boolean>(false);
  const [isShowPrivacy, setIsShowPrivacy] = useState<boolean>(false);
  const [termsAccepted, setTermAccepted] = useState<boolean>(false);
  const [privacyAccepted, setPrivacyAccepted] = useState<boolean>(false);
  const [isViewAgreement, setIsViewAgreement] = useState<boolean>(false);
  const [isForgotPinModalOpen, setIsForgotPinModalOpen] =
    useState<boolean>(false);

  const isEmailLoginButtonDisabled: boolean =
    email.trim() === '' || password.trim() === '';
  const isPinLoginButtonDisabled: boolean = pinLogin.length !== StaffPinLength;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isLoading) {
      console.log('[Login] Already in progress... skipping this request');
      return;
    }

    const loginResponse: LoginResponse | undefined = await login(
      email,
      password,
    );
    if (loginResponse) {
      if (loginResponse.hasMfa === true) {
        // User has MFA enabled and needs to verify OTP
        setActiveLoginKey(LoginToggleKey.ADMIN);
        navigateToVerifyOtp(email);
      } else {
        const tokenData = jwtDecode(loginResponse.accessToken) as any;

        if (
          !('type' in tokenData) ||
          tokenData.type === UserType.VENUE_STAFF ||
          tokenData.type === UserType.VENUE_BRANCH_LOGIN
        ) {
          // User is a staff member (or type cannot be determined, treat them as staff)
          // Require PIN for login
          // Store the staff access token in localStorage
          setIsDisplayPIN(true);
          setActiveLoginKey(LoginToggleKey.STAFF);
        } else {
          localStorage.setItem(
            StorageKey.VENUE_UNIT_DETAILS,
            JSON.stringify((loginResponse as any).venueUnitDetails),
          );

          if (loginResponse?.onboardingPending) {
            // Check if userStatus is invited, then navigate to onboarding page for profile setup
            // where it first accepts terms and conditions (onboarding page is wrapped in TnC wrapper)
            localStorage.setItem(
              StorageKey.ORGANIZATION_UNIT_DATA,
              JSON.stringify((loginResponse as any).organisationUnitDatas),
            );
            navigate(route.onboarding);
          } else {
            // TODO: Remove else condition after development is complete and always navigate to pin pad
            // User is not staff, proceed to dashboard

            // Check if user is venue admin/manager and if venue setup is needed( if they skipped setting it up earlier)
            if (
              (tokenData?.type === UserType.VENUE_ADMIN ||
                tokenData?.type === UserType.VENUE_MANAGER) &&
              loginResponse?.venueUnitDetails &&
              checkVenueSetupNeeded(loginResponse?.venueUnitDetails)
            ) {
              navigate(route.venueSetup);
            } else {
              navigate(route.dashboard);
            }
          }
        }
      }
    }
  };

  useEffect(() => {
    if (staffAccessToken) {
      setIsDisplayPIN(true);
    }
  }, [staffAccessToken]);

  const navigateToVerifyOtp = (email: string) => {
    navigate(route.verifyOtp, { state: { email, password } });
  };

  const handleToggle = (option: LoginToggleKey) => {
    setActiveLoginKey(option);
  };

  const onForgotClick = () => {
    navigate(route.forgotPassword);
  };

  const handleForgotPinClick = () => {
    setIsForgotPinModalOpen(true);
  };

  const handleAuthClick = () => {
    setIsForgotPinModalOpen(true);
  };

  const handlePrivacyBack = () => {
    setIsShowPrivacy(false);
    setIsShowTerm(true);
  };

  const handleTermsContinue = () => {
    setIsShowPrivacy(true);
    setIsShowTerm(false);
  };

  const handlePrivacyContinue = () => {
    setIsShowPrivacy(false);
  };

  const handleViewTermModal = () => {
    setIsShowTerm(true);
    setIsViewAgreement(true);
  };

  const handleViewPrivacyModal = () => {
    setIsShowPrivacy(true);
    setIsViewAgreement(true);
  };

  const handleStaffLogin = async (event: FormEvent) => {
    event.preventDefault();
    const staffLoginResponse: LoginAsStaffResponseData | undefined =
      await loginAsStaff(pinLogin);
    if (!staffLoginResponse) {
      console.error('Staff login failed: No response received from the API.');
      return;
    }
    if (staffLoginResponse.statusCode === apiStatusCode.SUCCESS) {
      // check if user needs to do t&c
      localStorage.setItem(
        StorageKey.MUST_ACCEPT_TNC,
        JSON.stringify(staffLoginResponse.data.mustAcceptTnC),
      );
      const staffAccessToken = localStorage.getItem(
        StorageKey.STAFF_ACCESS_TOKEN,
      );
      if (staffAccessToken) {
        const tokenData = jwtDecode(staffAccessToken) as any;
        if (
          (tokenData.type === UserType.VENUE_STAFF ||
            tokenData.type === UserType.VENUE_BRANCH_LOGIN) &&
          staffLoginResponse.data.onboardingPending
        ) {
          // Check if user is VENUE_STAFF & userStatus is onboardingPending, then navigate to onboarding page for profile setup
          // where it first accepts terms and conditions (onboarding page is wrapped in TnC wrapper)
          navigate(route.onboarding);
        } else {
          navigate(route.dashboard);
        }
      } else {
        navigate(route.dashboard);
      }
    }
  };

  return (
    <div className="relative min-h-screen w-full">
      {/* Desktop View */}
      {isShowTerm && (
        <AgreementModal
          data-testid="terms-modal"
          agreementTitle={'agreement.terms&condition'}
          agreementSubTitle={'agreement.terms&conditionSubTitle'}
          handleContinue={handleTermsContinue}
          handleOnClose={() => setIsShowTerm(false)}
          handleBack={() => setIsShowTerm(false)}
          content={'agreement.termsContent'}
          agreeAccepted={termsAccepted}
          setAgreeAccepted={setTermAccepted}
          isView={isViewAgreement}
          agreementText={'agreement.agreementTermsText'}
        />
      )}
      {isShowPrivacy && (
        <AgreementModal
          data-testid="privacy-modal"
          agreementTitle={'agreement.privacyPolicy'}
          agreementSubTitle={'agreement.privacyPolicySubTitle'}
          handleContinue={handlePrivacyContinue}
          handleOnClose={() => setIsShowPrivacy(false)}
          handleBack={handlePrivacyBack}
          content={'agreement.privacyContent'}
          agreeAccepted={privacyAccepted}
          setAgreeAccepted={setPrivacyAccepted}
          isView={isViewAgreement}
          agreementText={'agreement.agreementPrivacyText'}
        />
      )}

      {showResetSuccessToast && (
        <SuccessToast
          message={SuccessMessage.RESET_PASSWORD_SUCCESS}
          onClose={() => setShowResetSuccessToast(false)}
          data-testid="reset-password-toast"
          closeTestId="reset-password-toast-close"
        />
      )}

      {showNewPasswordSuccessToast && (
        <SuccessToast
          message={SuccessMessage.NEW_PASSWORD_SUCCESS}
          onClose={() => setShowNewPasswordSuccessToast(false)}
          data-testid="new-password-toast"
          closeTestId="new-password-toast-close"
        />
      )}

      {isDesktop
        ? renderDesktopView(
            activeLoginKey,
            handleToggle,
            email,
            setEmail,
            password,
            setPassword,
            loginTranslation,
            isPinLoginButtonDisabled,
            isEmailLoginButtonDisabled,
            isLoading,
            handleSubmit,
            showPassword,
            setShowPassword,
            selectedLanguage,
            setSelectedLanguage,
            onForgotClick,
            pinLogin,
            setPinLogin,
            handleForgotPinClick,
            isDisplayPIN,
            error,
            handleViewTermModal,
            handleViewPrivacyModal,
            handleStaffLogin,
            loginAsStaffLoading,
            loginAsStaffError,
          )
        : renderTabletView(
            handleViewTermModal,
            handleViewPrivacyModal,
            activeLoginKey,
            handleToggle,
            isDisplayPIN,
            StaffPinLength,
            'login.forgotPin',
            onForgotClick,
            handleAuthClick,
            email,
            setEmail,
            password,
            setPassword,
            loginTranslation,
            isEmailLoginButtonDisabled,
            isLoading,
            handleSubmit,
            showPassword,
            setShowPassword,
            pinLogin,
            setPinLogin,
            error,
            handleStaffLogin,
            loginAsStaffLoading,
            loginAsStaffError,
          )}

      <ForgotPinModal
        isOpen={isForgotPinModalOpen}
        onClose={() => setIsForgotPinModalOpen(false)}
      />
    </div>
  );
};
export default Login;
